import React, { useCallback, useEffect, useRef, useState } from 'react'
import axios from 'axios'
import type { CancelTokenSource } from 'axios'
import { Button, message, Space, Spin } from 'antd'
import { useIntl } from 'react-intl'
import SearchConditions from './SearchConditions'
import type { SearchConditionErrors, SearchConditionsDraft } from './types'
import type { ReportFactoryConfig } from './reportFactoryTypes'
import { getReportFactoryConfig } from './reportFactoryService'
import {
  getReportErrorKey,
  useReportFactoryExport,
} from './useReportFactoryExport'
import type { ExportFeedback } from './useReportFactoryExport'
import {
  buildInitialConditions,
  getPortfolioOptions,
} from './utils/buildInitialConditions'
import { buildReportFactoryRequest } from './utils/buildReportFactoryRequest'
import { validateSearchConditions } from './utils/validateSearchConditions'

const EMPTY_CONDITIONS: SearchConditionsDraft = {
  baseDate: '',
  rows: [
    { rowId: 'row-1', portfolioIds: [], benchmarkInput: '3' },
    { rowId: 'row-2', portfolioIds: [], benchmarkInput: '0' },
  ],
  groupBy: 'Currency',
  isAppliedAccountMTD: false,
  useFullFundName: false,
}

const MTDPerformanceReport = (): JSX.Element => {
  const { formatMessage } = useIntl()
  const t = (id: string) => formatMessage({ id })
  const [conditions, setConditions] =
    useState<SearchConditionsDraft>(EMPTY_CONDITIONS)
  const [errors, setErrors] = useState<SearchConditionErrors>({ rows: {} })
  const [config, setConfig] = useState<ReportFactoryConfig>()
  const [configLoading, setConfigLoading] = useState(true)
  const [configFailure, setConfigFailure] = useState<string>()
  const [localFailure, setLocalFailure] = useState<string>()
  const nextRowId = useRef(3)
  const mounted = useRef(true)
  const initialized = useRef(false)
  const configGeneration = useRef(0)
  const configLoadingRef = useRef(true)
  const configCancellation = useRef<CancelTokenSource>()

  const loadConfig = useCallback(async () => {
    const request = ++configGeneration.current
    configCancellation.current?.cancel()
    const cancellation = axios.CancelToken.source()
    configCancellation.current = cancellation
    configLoadingRef.current = true
    setConfigLoading(true)
    setConfigFailure(undefined)
    try {
      const next = await getReportFactoryConfig({
        cancelToken: cancellation.token,
      })
      if (!mounted.current || request !== configGeneration.current) return
      setConfig(next)
      if (!initialized.current) {
        const initial = buildInitialConditions(next)
        setConditions(initial)
        nextRowId.current = initial.rows.length + 1
        initialized.current = true
      }
    } catch (error) {
      if (!mounted.current || request !== configGeneration.current) return
      setConfigFailure(getReportErrorKey(error))
      throw error
    } finally {
      if (mounted.current && request === configGeneration.current) {
        configLoadingRef.current = false
        setConfigLoading(false)
      }
    }
  }, [])

  useEffect(() => {
    mounted.current = true
    void loadConfig().catch(() => undefined)
    return () => {
      mounted.current = false
      configGeneration.current += 1
      configCancellation.current?.cancel()
    }
  }, [loadConfig])

  const exporter = useReportFactoryExport(loadConfig)
  const locked =
    !config ||
    configLoading ||
    Boolean(configFailure) ||
    exporter.conditionsLocked
  const unavailable = config
    ? conditions.rows.filter((row) =>
        row.portfolioIds.some(
          (id) =>
            !config.portfolios.some((portfolio) => portfolio.portfolioId === id)
        )
      )
    : []
  const displayedErrors: SearchConditionErrors = {
    ...errors,
    rows: {
      ...errors.rows,
      ...Object.fromEntries(
        unavailable.map((row) => [
          row.rowId,
          { ...errors.rows[row.rowId], portfolioIds: 'unavailable' as const },
        ])
      ),
    },
  }

  const mayEdit = () =>
    !configLoadingRef.current &&
    config &&
    !configFailure &&
    !exporter.isLocked()
  const clearErrors = () => {
    setErrors({ rows: {} })
    setLocalFailure(undefined)
    exporter.clearFeedback()
  }
  const changeConditions = (next: SearchConditionsDraft) => {
    if (!mayEdit()) return
    setConditions(next)
    clearErrors()
  }
  const addRow = () => {
    if (!mayEdit()) return
    const rowId = 'row-' + nextRowId.current++
    setConditions((current) =>
      current.rows.length >= 6
        ? current
        : {
            ...current,
            rows: [
              ...current.rows,
              { rowId, portfolioIds: [], benchmarkInput: '0' },
            ],
          }
    )
    clearErrors()
  }
  const removeRow = (rowId: string) => {
    if (
      !mayEdit() ||
      conditions.rows.length <= 1 ||
      !conditions.rows.some((row) => row.rowId === rowId)
    ) {
      return
    }

    setConditions((current) => ({
      ...current,
      rows: current.rows.filter((row) => row.rowId !== rowId),
    }))
    setErrors((current) => {
      const remainingErrors = { ...current.rows }
      delete remainingErrors[rowId]
      return { ...current, rows: remainingErrors }
    })
    setLocalFailure(undefined)
    exporter.clearFeedback()
  }
  const searchReport = () => {
    if (!mayEdit() || !config || unavailable.length) return
    const result = validateSearchConditions(conditions)
    setErrors(result.isValid ? { rows: {} } : result.errors)
    if (!result.isValid) return
    try {
      const request = buildReportFactoryRequest(result.value, config)
      setLocalFailure(undefined)
      void exporter.submit(request)
    } catch (error) {
      setLocalFailure(getReportErrorKey(error))
    }
  }
  const feedback: ExportFeedback | undefined = localFailure
    ? { type: 'error' as const, key: localFailure }
    : exporter.feedback ||
      (configFailure
        ? { type: 'error' as const, key: configFailure }
        : undefined)
  const retryKeys = {
    create: 'hkReport.retry.request',
    status: 'hkReport.retry.status',
    download: 'hkReport.retry.download',
  }
  const feedbackType = feedback?.type
  const feedbackKey = feedback?.key
  const portfolioNames = feedback?.portfolioNames
  useEffect(() => {
    if (
      feedbackKey &&
      (feedbackType === 'error' || feedbackType === 'warning')
    ) {
      const close = message[feedbackType](
        formatMessage({ id: feedbackKey }, { portfolios: portfolioNames }),
        10
      )
      return () => {
        close()
      }
    }
  }, [feedbackKey, feedbackType, portfolioNames, formatMessage])

  return (
    <div className="content-wrapper">
      <SearchConditions
        conditionsDisabled={locked}
        errors={displayedErrors}
        onAddRow={addRow}
        onChange={changeConditions}
        onRemoveRow={removeRow}
        onSearch={searchReport}
        portfolioSelectionDisabled={locked}
        portfolios={config ? getPortfolioOptions(config) : []}
        searchDisabled={locked || unavailable.length > 0}
        searchLoading={exporter.busy}
        t={t}
        value={conditions}
      />
      {exporter.downloadJobId && (
        <div style={{ display: 'flex', margin: 18, alignItems: 'center' }}>
          <Button
            aria-label={t('hkReport.download.excel')}
            disabled={configLoading || exporter.busy}
            onClick={() => {
              void exporter.downloadExport()
            }}
            style={{ padding: 0, height: 'auto' }}
            type="text"
          >
            {exporter.phase === 'downloading' ? (
              <Spin size="small" />
            ) : (
              <img
                alt={t('hkReport.download.excel')}
                height={35}
                src="/assets/images/export.png"
                width={35}
              />
            )}
          </Button>
        </div>
      )}
      <Space wrap>
        {configFailure && (
          <Button
            disabled={exporter.busy}
            loading={configLoading}
            onClick={() => {
              exporter.clearFeedback()
              void loadConfig().catch(() => undefined)
            }}
          >
            {t('hkReport.retry.config')}
          </Button>
        )}
        {exporter.retry && (
          <Button
            disabled={configLoading || exporter.busy}
            onClick={() => {
              void exporter.retryExport()
            }}
          >
            {t(retryKeys[exporter.retry.kind])}
          </Button>
        )}
        {exporter.retry?.kind === 'create' && (
          <Button onClick={exporter.startNewReport}>
            {t('hkReport.newReport')}
          </Button>
        )}
      </Space>
    </div>
  )
}

export default MTDPerformanceReport
